<template>
    <v-card class="ma-2 overflow-hidden">
        <div class="d-flex justify-space-around custom-header-row">
            <span class="custom-header-cell">Part Number</span>
            <span class="custom-header-cell">Quantity Requested</span>
            <span class="custom-header-cell">Date Requested</span>
            <span class="custom-header-cell"> </span>
        </div>
        <div class="scroll-container">
            <div
                v-if="loading"
                class="d-flex justify-center align-center fill-height"
            >
                <LoadingComponent />
            </div>
            <v-virtual-scroll
                v-else
                :items="filteredItems"
                class="scroll-content"
                :style="{
                    animationDuration: shouldScroll
                        ? `${scrollDuration}s`
                        : '0s',
                    animationPlayState: startAnimation ? 'running' : 'paused',
                }"
            >
                <template #default="{ item, index }">
                    <div
                        class="d-flex justify-space-around align-center text-center row-item"
                        :style="{
                            backgroundColor: item.rowColor || 'lightgrey',
                        }"
                    >
                        <span class="custom-cell">
                            <b>{{ item.PartNum.toUpperCase() }}</b
                            ><br />
                            <span
                                class="cell-subtitle clamp-3"
                                :title="item.Description"
                            >
                                {{ item.Description }}
                            </span>
                        </span>
                        <span class="custom-cell text-h5 font-weight-bold">
                            {{ item.RequestQuantity }}
                        </span>
                        <span class="custom-cell">
                            {{ formatDate(item.TimeRequested) }}<br />
                            {{ formatTime(item.TimeRequested) }}
                        </span>
                        <span class="d-flex gap-2 custom-cell">
                            <span v-if="item.InTruck">
                                <v-img
                                    src="/images/Truck.png"
                                    title="Material on truck"
                                    :height="50"
                                />
                            </span>
                            <span
                                v-else-if="
                                    item.ToBuilding !== item.FromBuilding &&
                                    item.TransferPending
                                "
                            >
                                <v-img
                                    src="/images/MysteryMachine.png"
                                    title="Material Not Yet On Truck"
                                    :height="50"
                                />
                            </span>
                            <span v-if="item.ToBuilding !== item.FromBuilding">
                                <v-img
                                    :src="`/images/To${item.ToBuilding}.png`"
                                    title="Transfer To"
                                    :height="50"
                                />
                            </span>
                        </span>
                    </div>
                    <v-divider class="border-opacity-100"></v-divider>
                </template>
            </v-virtual-scroll>
            <div
                v-if="!loading && filteredItems.length === 0"
                class="d-flex justify-center align-center fill-height"
            >
                <b style="color: red; font-size: 1.5em"
                    >No Requests at this time</b
                >
            </div>
        </div>
        <div class="d-flex justify-space-between px-2 custom-footer">
            <b style="min-width: 196px"></b>
            <b
                >LIST REFRESHES EVERY 10 MINUTES. CHECK REQUEST LIST FOR REAL
                TIME UPDATES.</b
            >
            <b style="min-width: 196px"
                >Showing {{ filteredItems.length }} Requested Parts</b
            >
        </div>
    </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { defineAsyncComponent } from "vue";

const GuestLayout = defineAsyncComponent(() =>
    import("../../../Layouts/GuestLayout.vue")
);

const LoadingComponent = defineAsyncComponent(() =>
    import("../../../components/LoadingComponent.vue")
);
export default {
    layout: [GuestLayout],
    components: { LoadingComponent },
    name: "LogisticsTV",
    data() {
        return {
            building: this.getBuilding(),
            loading: false,
            items: [],
            baseDuration: 10,
            durationPerItem: 4,
            interval: null,
            startAnimation: false,
        };
    },
    computed: {
        filteredItems() {
            const filtered = this.items.filter((item) => {
                // Check the conditions for RequestType and MaterialType, similar to the legacy code
                item.rowColor = "lightgrey";
                if (item.ToBuilding !== item.FromBuilding) {
                    item.rowColor = "rgb(198,239,206)";
                } else if (["N", "L", "H"].indexOf(item.RequestType) !== -1) {
                    // Newstores, Hotline, ?
                    switch (item.MaterialType) {
                        case "M":
                            item.rowColor = "#bce0ff";
                            break;
                        case "P":
                            item.rowColor = "#fbffcd";
                            return true;
                    }
                } else {
                    switch (item.RequestType) {
                        case "E":
                            item.rowColor = "RGBA(156,39,176,.26)";
                            break;
                        case "C":
                            item.rowColor = "#2196F3";
                            break;
                        default:
                            return false; // Hide this row
                    }
                }

                // Hide rows where InEpicor, InWarehouse, and InTruck are all 0
                if (
                    item.InEpicor === 0 &&
                    item.InWarehouse === 0 &&
                    item.InTruck === 0
                ) {
                    return false; // Hide this row
                }

                if (item.Priority) {
                    item.rowColor = "rgb(245, 37, 37)";
                }

                // Automatically include rows that don't match any hide condition
                return true;
            });

            return filtered.sort((a, b) => {
                // First, sort by Priority in descending order
                if (a.Priority < b.Priority) return 1; // Descending order for Priority
                if (a.Priority > b.Priority) return -1;

                // If Priority is the same, sort by TimeRequested in ascending order
                if (a.TimeRequested < b.TimeRequested) return -1; // Ascending order for TimeRequested
                if (a.TimeRequested > b.TimeRequested) return 1;

                return 0; // If both are equal, leave their order unchanged
            });
        },
        shouldScroll() {
            return this.filteredItems.length > 6;
        },
        scrollDuration() {
            // Calculate duration based on the number of items
            return (
                this.baseDuration +
                this.filteredItems.length * this.durationPerItem
            );
        },
    },
    watch: {
        filteredItems() {
            this.delayScroll(); // Restart delay when data changes
        },
    },
    mounted() {
        this.fetchRequestList();

        this.interval = setInterval(() => {
            this.fetchRequestList();
        }, 10 * 60 * 1000); // 10 minutes in milliseconds
    },
    beforeUnmount() {
        // Clear the interval when the component is destroyed
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    },
    methods: {
        getBuilding() {
            let path = window.location.pathname;
            let bldg = path.substring(path.length - 2);

            return bldg;
        },
        async fetchRequestList() {
            this.loading = true;

            await axios
                .get(
                    route("legacy-api.logistics-getRequestsFromEpicor", {
                        View: 0,
                        Method: "List",
                        Warehouse: this.building,
                        SubDepartment: "%",
                        Requestor: "%",
                        PartNum: "%",
                        Start: "",
                        End: "",
                        FulfilledBy: "%",
                        SortBy: "part",
                    })
                )
                .then((res) => {
                    this.items = res.data.data.data;
                })
                .finally(() => {
                    this.loading = false;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        delayScroll() {
            this.startAnimation = false;
            setTimeout(() => {
                this.startAnimation = true;
            }, 5000); // 5-second delay
        },
        formatDate(date) {
            return moment(date).format("MM/DD/YYYY");
        },
        formatTime(date) {
            return moment(date).format("LTS");
        },
    },
};
</script>

<style scoped>
.custom-header-row {
    text-align: center;
    background-color: #2196f3;
    color: white;
    font-size: 2em;
}

.custom-header-cell {
    width: 25%;
}

.row-item {
    height: 125px;
}

.custom-cell {
    width: 25%;
    font-size: 1.2em;
}

.cell-subtitle {
    font-size: 0.75em;
    color: #424242;
}

.scroll-container {
    height: calc(100vh - 64px - 48px - 24px - 16px);
    overflow: hidden;
    position: relative;
}

.scroll-content {
    width: 100%;
    position: absolute;
    will-change: transform;
    animation: scroll-animation 5s linear infinite;
}

@keyframes scroll-animation {
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(-100%);
    }
}

.custom-footer {
    background-color: #2196f3;
    color: white;
}

.clamp-3 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
}
</style>
